import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * DetailLayout — the shared scaffold for drill-in / detail pages so they
 * stop looking like sparse left-aligned stacks. A main column for the
 * primary content and an optional sidebar (rail) for supporting context
 * (status, liaison card, next steps). Collapses to a single column on
 * small screens.
 */
export interface DetailLayoutProps {
  /** Primary content column. */
  children: React.ReactNode;
  /** Supporting rail (right on desktop, below on mobile). */
  aside?: React.ReactNode;
  /** Header region spanning both columns (e.g. a PageHeader). */
  header?: React.ReactNode;
  className?: string;
}

export function DetailLayout({ children, aside, header, className }: DetailLayoutProps) {
  return (
    <div className={cn("flex flex-col gap-space-4", className)}>
      {header}
      <div
        className={cn(
          "grid grid-cols-1 gap-space-4",
          aside && "lg:grid-cols-[minmax(0,1fr)_320px]",
        )}
      >
        <div className="flex min-w-0 flex-col gap-space-4">{children}</div>
        {aside && <aside className="flex flex-col gap-space-4">{aside}</aside>}
      </div>
    </div>
  );
}

/**
 * DetailSection — a titled card block used inside DetailLayout to give
 * related content (history, comments, outstanding items) clear, consistent
 * grouping instead of bare headings.
 */
export interface DetailSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title?: React.ReactNode;
  /** Right-aligned header accessory (count, action). */
  accessory?: React.ReactNode;
}

export function DetailSection({
  title,
  accessory,
  className,
  children,
  ...props
}: DetailSectionProps) {
  return (
    <section
      className={cn(
        "rounded-lg border border-border-light bg-white-pure p-space-4 shadow-sm",
        className,
      )}
      {...props}
    >
      {(title || accessory) && (
        <div className="mb-space-3 flex items-center justify-between gap-space-2">
          {title && (
            <h2 className="m-0 font-display text-h3 font-bold text-onyx">{title}</h2>
          )}
          {accessory}
        </div>
      )}
      {children}
    </section>
  );
}
